import { combineReducers } from 'redux';
import { IState } from '@redux/reducer';
import { IntegrationOrgConfig } from '@liquid-state/pephealth-client';

import {
  ILoadOrgConfig,
  ILoadOrgConfigSuccess,
  ILoadOrgConfigNotConfigured,
  ILoadOrgConfigFailure,
  ICreateOrgConfig,
  ICreateOrgConfigSuccess,
  ICreateOrgConfigFailure,
  IEditOrgConfig,
  IEditOrgConfigSuccess,
  IEditOrgConfigFailure,
} from './actions';

type IntegrationRequestStatus = 'idle' | 'loading' | 'success' | 'not-configured' | 'failure';

interface IntegrationRequestState {
  status: IntegrationRequestStatus;
  isLoading: boolean;
  isSubmitting: boolean;
  errorStatus?: number;
  errorCode?: string;
  submissionErrorStatus?: number;
  submissionErrorCode?: string;
}

const CURRENT_ORGANISATION_TARGET = '__current-organisation__';

const initialRequestState: IntegrationRequestState = {
  status: 'idle',
  isLoading: false,
  isSubmitting: false,
};

type TargetStatusState = Record<string, Record<string, IntegrationRequestState>>;
type TargetConfigState = Record<string, Record<string, IntegrationOrgConfig>>;

const targetKey = (organisationId?: string) => organisationId || CURRENT_ORGANISATION_TARGET;

const updateTargetIntegration = <T>(
  state: Record<string, Record<string, T>>,
  organisationId: string | undefined,
  integrationSlug: string,
  value: T,
) => {
  const target = targetKey(organisationId);

  return {
    ...state,
    [target]: {
      ...(state[target] || {}),
      [integrationSlug]: value,
    },
  };
};

const removeTargetIntegration = <T>(
  state: Record<string, Record<string, T>>,
  organisationId: string | undefined,
  integrationSlug: string,
) => {
  const target = targetKey(organisationId);
  const targetState = { ...(state[target] || {}) };
  delete targetState[integrationSlug];

  return {
    ...state,
    [target]: targetState,
  };
};

const status = (
  state: TargetStatusState = {},
  action:
    | ILoadOrgConfig
    | ILoadOrgConfigSuccess
    | ILoadOrgConfigNotConfigured
    | ILoadOrgConfigFailure
    | ICreateOrgConfig
    | ICreateOrgConfigSuccess
    | ICreateOrgConfigFailure
    | IEditOrgConfig
    | IEditOrgConfigSuccess
    | IEditOrgConfigFailure,
) => {
  const payload =
    'payload' in action
      ? (action.payload as {
          integrationSlug?: string;
          organisationId?: string;
          status?: number;
          errorCode?: string;
        })
      : {};
  const integrationSlug = payload.integrationSlug;
  if (!integrationSlug) {
    return state;
  }
  const target = targetKey(payload.organisationId);
  const current = state[target]?.[integrationSlug] || initialRequestState;

  switch (action.type) {
    case 'integrations/load-org-config':
      return updateTargetIntegration<IntegrationRequestState>(
        state,
        payload.organisationId,
        integrationSlug,
        {
          ...current,
          status: 'loading',
          isLoading: true,
          errorStatus: undefined,
          errorCode: undefined,
        },
      );
    case 'integrations/load-org-config-success':
      return updateTargetIntegration<IntegrationRequestState>(
        state,
        payload.organisationId,
        integrationSlug,
        {
          ...current,
          status: 'success',
          isLoading: false,
          errorStatus: undefined,
          errorCode: undefined,
        },
      );
    case 'integrations/load-org-config-not-configured':
      return updateTargetIntegration<IntegrationRequestState>(
        state,
        payload.organisationId,
        integrationSlug,
        {
          ...current,
          status: 'not-configured',
          isLoading: false,
          errorStatus: undefined,
          errorCode: undefined,
        },
      );
    case 'integrations/load-org-config-failure':
      return updateTargetIntegration<IntegrationRequestState>(
        state,
        payload.organisationId,
        integrationSlug,
        {
          ...current,
          status: 'failure',
          isLoading: false,
          errorStatus: payload.status,
          errorCode: payload.errorCode,
        },
      );
    case 'integrations/create-org-config':
    case 'integrations/edit-org-config':
      return updateTargetIntegration<IntegrationRequestState>(
        state,
        payload.organisationId,
        integrationSlug,
        {
          ...current,
          isSubmitting: true,
          submissionErrorStatus: undefined,
          submissionErrorCode: undefined,
        },
      );
    case 'integrations/create-org-config-success':
    case 'integrations/edit-org-config-success':
      return updateTargetIntegration<IntegrationRequestState>(
        state,
        payload.organisationId,
        integrationSlug,
        {
          ...current,
          status: 'success',
          isSubmitting: false,
          submissionErrorStatus: undefined,
          submissionErrorCode: undefined,
        },
      );
    case 'integrations/create-org-config-failure':
    case 'integrations/edit-org-config-failure':
      return updateTargetIntegration<IntegrationRequestState>(
        state,
        payload.organisationId,
        integrationSlug,
        {
          ...current,
          isSubmitting: false,
          submissionErrorStatus: payload.status,
          submissionErrorCode: payload.errorCode,
        },
      );
    default:
      return state;
  }
};

const orgConfigs = (
  state: TargetConfigState = {},
  action:
    | ILoadOrgConfigSuccess
    | ICreateOrgConfigSuccess
    | IEditOrgConfigSuccess
    | ILoadOrgConfigNotConfigured
    | ILoadOrgConfigFailure,
) => {
  switch (action.type) {
    case 'integrations/load-org-config-success':
    case 'integrations/create-org-config-success':
    case 'integrations/edit-org-config-success':
      return updateTargetIntegration(
        state,
        action.payload.organisationId,
        action.payload.integrationSlug,
        action.payload.config,
      );
    case 'integrations/load-org-config-not-configured':
    case 'integrations/load-org-config-failure':
      return removeTargetIntegration(
        state,
        action.payload.organisationId,
        action.payload.integrationSlug,
      );
    default:
      return state;
  }
};

export const selectIsLoading = (state: IState) => {
  const currentOrganisationRequests = state.integrations.status[CURRENT_ORGANISATION_TARGET] || {};
  return Object.values(currentOrganisationRequests).some(request => request.isLoading);
};

export const selectIsSubmitting = (state: IState) => {
  const currentOrganisationRequests = state.integrations.status[CURRENT_ORGANISATION_TARGET] || {};
  return Object.values(currentOrganisationRequests).some(request => request.isSubmitting);
};

const organisationTarget = (organisation?: string | { slug?: string }) =>
  typeof organisation === 'string' ? organisation : organisation?.slug;

export const selectIntegrationConfiguration = (
  integrationSlug: string,
  organisation?: string | { slug?: string },
) => (state: IState) => {
  const target = targetKey(organisationTarget(organisation));
  const request = state.integrations.status[target]?.[integrationSlug] || initialRequestState;

  return {
    ...request,
    config: state.integrations.orgConfigs[target]?.[integrationSlug],
  };
};

export const selectOrgConfig = (
  integrationSlug: string,
  organisation?: string | { slug?: string },
) => (state: IState): [boolean, IntegrationOrgConfig | undefined] => {
  const configuration = selectIntegrationConfiguration(integrationSlug, organisation)(state);
  return [configuration.isLoading, configuration.config];
};

export default combineReducers({
  status,
  orgConfigs,
});
